import { getApps, initializeApp } from "firebase/app";
import { type Auth, getAuth, onAuthStateChanged, signInWithCustomToken, signOut } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import {
  acceptInvite,
  inviteError,
  type InviteView,
  inviteViewText,
  keepInvite,
  keptInvite,
  linkError,
  meError,
  peekInvite,
  PORTAL_HEADING,
  PORTAL_OFF,
  portalBootstrap,
  portalCodes,
  portalLink,
  type PortalMe,
  portalMe,
  portalRedeem,
  portalStart,
  SIGN_IN_FIRST,
  SIGN_IN_PATH,
  signInTitle,
  validEmail,
} from "./portal";
import { PortalShell } from "./PortalPages";
import type { PortalTab } from "./portalView";
import { MAX_EMAIL } from "./start";
import { Skeleton } from "./Skeleton";

const APP_NAME = "customers";

type Step =
  | { kind: "loading" }
  | { kind: "off" }
  | { kind: "invite"; code: string; view: InviteView }
  | { kind: "email"; sent: string | null }
  | { kind: "in"; me: PortalMe }
  | { kind: "problem"; text: string };

function portalAuth(identity: { api_key: string; auth_domain: string; tenant_id: string }): Auth {
  const app =
    getApps().find((found) => found.name === APP_NAME) ??
    initializeApp({ apiKey: identity.api_key, authDomain: identity.auth_domain }, APP_NAME);
  const auth = getAuth(app);
  auth.tenantId = identity.tenant_id;
  return auth;
}

function clearHash() {
  history.replaceState(null, "", location.pathname + location.search);
}

function firstUser(auth: Auth): Promise<boolean> {
  return new Promise((resolve) => {
    const stop = onAuthStateChanged(auth, (user) => {
      stop();
      resolve(user !== null);
    });
  });
}

/** Roadmap 080: accept an invite or sign in, then show the portal pages (C3). */
export function PortalPage({ tab = "home" }: { tab?: PortalTab }) {
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [round, setRound] = useState(0);
  const auth = useRef<Auth | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (step.kind !== "in") document.title = signInTitle(step.kind);
  }, [step.kind]);
  useEffect(() => {
    heading.current?.focus();
  }, [step.kind]);

  const showMe = async (found: Auth) => {
    const user = found.currentUser;
    if (!user) return setStep({ kind: "email", sent: null });
    const kept = keptInvite();
    if (kept) {
      keepInvite(null);
      try {
        console.info("portal: the kept invite is shown again after the sign-in");
        return setStep({ kind: "invite", code: kept, view: await peekInvite(kept) });
      } catch (error) {
        console.warn("portal: the kept invite is not valid", error);
      }
    }
    try {
      setStep({ kind: "in", me: await portalMe(await user.getIdToken()) });
    } catch (error) {
      console.warn("portal: /api/portal/me failed", error);
      setStep({ kind: "problem", text: meError(error) });
    }
  };

  // A sign-in link that opens in a tab already on /portal changes only the hash.
  useEffect(() => {
    const again = () => {
      if (!location.hash) return;
      console.info("portal: the hash changed; start again");
      setRound((n) => n + 1);
    };
    addEventListener("hashchange", again);
    return () => removeEventListener("hashchange", again);
  }, []);

  useEffect(() => {
    let live = true;
    setStep({ kind: "loading" });
    (async () => {
      let boot;
      try {
        boot = await portalBootstrap();
      } catch (error) {
        console.warn("portal: bootstrap failed", error);
        if (live) setStep({ kind: "off" });
        return;
      }
      const found = portalAuth(boot.identity);
      auth.current = found;
      const codes = portalCodes();
      const start = portalStart(location.hash, await firstUser(found));
      console.info(`portal: start ${start}`);
      if (start === "invite" && codes.invite) {
        try {
          const view = await peekInvite(codes.invite);
          if (live) setStep({ kind: "invite", code: codes.invite, view });
        } catch (error) {
          clearHash();
          if (live) setStep({ kind: "problem", text: inviteError(error) });
        }
      } else if (start === "link" && codes.link) {
        clearHash();
        try {
          await signInWithCustomToken(found, (await portalRedeem(codes.link)).custom_token);
          if (live) await showMe(found);
        } catch (error) {
          if (live) setStep({ kind: "problem", text: linkError(error) });
        }
      } else if (start === "signed-in") {
        if (live) await showMe(found);
      } else if (live) setStep({ kind: "email", sent: null });
    })();
    return () => {
      live = false;
    };
  }, [round]);

  const accept = async () => {
    if (step.kind !== "invite" || !auth.current) return;
    setBusy(true);
    try {
      const signedIn = auth.current.currentUser;
      const done = await acceptInvite(step.code, signedIn ? await signedIn.getIdToken() : null);
      clearHash();
      if (done.sign_in_first) {
        console.info("portal: the invite email has a portal account; sign in first");
        keepInvite(step.code);
        return setStep({ kind: "email", sent: SIGN_IN_FIRST });
      }
      if (done.custom_token) await signInWithCustomToken(auth.current, done.custom_token);
      console.info("portal: invite accepted");
      await showMe(auth.current);
    } catch (error) {
      clearHash();
      setStep({ kind: "problem", text: inviteError(error) });
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (event: FormEvent) => {
    event.preventDefault();
    if (!validEmail(email)) return setStep({ kind: "email", sent: "Enter your email address." });
    setBusy(true);
    try {
      await portalLink(email.trim());
      setStep({
        kind: "email",
        sent: `If ${email.trim()} has a portal account, we sent a sign-in link to it. Open the link within 15 minutes.`,
      });
    } catch (error) {
      setStep({ kind: "email", sent: linkError(error) });
    } finally {
      setBusy(false);
    }
  };

  const signOutNow = () => {
    if (!auth.current) return;
    console.info("portal: sign out");
    signOut(auth.current).then(() => {
      history.replaceState(null, "", SIGN_IN_PATH);
      dispatchEvent(new PopStateEvent("popstate"));
      setStep({ kind: "email", sent: null });
    });
  };
  const token = async () => {
    const user = auth.current?.currentUser;
    if (!user) throw new Error("portal: no signed-in user");
    return user.getIdToken();
  };
  if (step.kind === "in") return <PortalShell me={step.me} tab={tab} token={token} onSignOut={signOutNow} />;

  return (
    <main className="portal" data-testid="portal-page">
      <h1 ref={heading} tabIndex={-1} data-testid="portal-sign-in-title">
        {PORTAL_HEADING}
      </h1>
      <p className="sub" data-testid="portal-about">
        See the containers and the oil pickups of your restaurant, and ask your collector for a pickup.
      </p>
      {step.kind === "loading" && <Skeleton shape="form" />}
      {step.kind === "off" && (
        <p role="alert" data-testid="portal-off">
          {PORTAL_OFF}
        </p>
      )}
      {step.kind === "problem" && (
        <>
          <p role="alert" className="error" data-testid="portal-problem">
            {step.text}
          </p>
          <p className="actions">
            <button type="button" onClick={() => setStep({ kind: "email", sent: null })}>
              Sign in with your email address
            </button>
          </p>
        </>
      )}
      {step.kind === "invite" && (
        <section aria-labelledby="portal-invite-title" data-testid="portal-invite">
          <h2 id="portal-invite-title">Your invite</h2>
          <p data-testid="portal-invite-text">{inviteViewText(step.view)}</p>
          <p className="actions">
            <button className="primary" type="button" data-testid="portal-accept" disabled={busy} onClick={accept}>
              {busy ? "Accepting…" : "Accept and sign in"}
            </button>
          </p>
        </section>
      )}
      {step.kind === "email" && (
        <form onSubmit={sendLink} noValidate data-testid="portal-email-form">
          <label htmlFor="portal-email">Email address</label>
          <input
            id="portal-email"
            type="email"
            autoComplete="email"
            maxLength={MAX_EMAIL}
            value={email}
            data-testid="portal-email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className="actions">
            <button className="primary" disabled={busy} data-testid="portal-send-link">
              {busy ? "Sending…" : "Email me a sign-in link"}
            </button>
          </p>
          <p role="status" data-testid="portal-email-status">
            {step.sent}
          </p>
        </form>
      )}
      <p className="sub" data-testid="portal-collector-link">
        Work for a collector? <a href="/">Sign in here</a>
      </p>
    </main>
  );
}
